// Account route — '/account'.
//
// Auth gate:
//   - `beforeLoad` runs BEFORE the component mounts. We read the token
//     directly from the persisted auth store and `throw redirect(...)`
//     if it's missing. Throwing redirect is the canonical TanStack
//     Router pattern for synchronous guards.
//   - Reading from useAuthStore.getState() (not the hook) is intentional:
//     beforeLoad runs outside React's render tree.
//
// Data:
//   - Once mounted, useQuery hits GET /api/account/ which returns
//     `{ success: true, email, name }`. We cache by key 'account'.
//
// Sign-out:
//   - Delegated to the shared `useSignOut()` hook in
//     `@/components/layout/user-menu` so this page and the global header
//     trigger the exact same flow (clear auth store → clear query cache
//     → navigate to /login with replace).
import { createFileRoute, redirect } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { InlineError } from '@/components/ui'
import { useSignOut } from '@/components/layout/user-menu'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { apiClient } from '@/lib/api-client'
import { useAuthStore } from '@/stores/auth-store'
import type { AccountInfo } from '@/lib/types'

type AccountResponse = AccountInfo & { success?: true }

export const Route = createFileRoute('/account')({
  beforeLoad: () => {
    if (!useAuthStore.getState().token) {
      throw redirect({ to: '/login', replace: true })
    }
  },
  component: AccountPage,
})

function AccountPage() {
  useDocumentTitle('Account')
  const { data, isLoading, isError } = useQuery<AccountResponse>({
    queryKey: ['account'],
    queryFn: () => apiClient.get<AccountResponse>('/account/'),
  })

  const handleSignOut = useSignOut()

  return (
    <main className="mx-auto w-full max-w-2xl px-4 py-8 sm:px-6">
      <Card className="w-full">
        <CardHeader className="space-y-1">
          <h1 className="text-2xl font-semibold leading-tight">Account</h1>
          <p className="text-sm text-muted-foreground">
            The identity attached to this session.
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-5 w-1/3" />
              <Skeleton className="h-5 w-2/3" />
            </div>
          ) : isError || !data ? (
            <InlineError message="Couldn't load account details. Try refreshing." />
          ) : (
            <dl className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-[13px] text-muted-foreground">Name</dt>
                <dd className="mt-1 text-foreground">{data.name}</dd>
              </div>
              <div>
                <dt className="text-[13px] text-muted-foreground">Email</dt>
                <dd className="mt-1 break-words text-foreground">{data.email}</dd>
              </div>
            </dl>
          )}
          <div className="flex justify-end border-t border-border pt-4">
            <Button
              variant="outline"
              onClick={handleSignOut}
              data-testid="account-signout"
            >
              Sign out
            </Button>
          </div>
        </CardContent>
      </Card>
    </main>
  )
}
